import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { useFeed } from '@/lib/use-feed';
import { ArticleFeed } from '@/components/content';
import { PageHeader, Section } from './page-parts';
import NotFound from './NotFound';

/**
 * `/:locale/:categorySlug` — §1.6.
 *
 * This route is last in the table because it matches any single segment; the reserved
 * slug list is what stops a category from ever taking a static route's name. The server
 * has already resolved the category and the first page of stories (`ssr-data.ts`), and
 * answers 404 when the slug names nothing — so by the time this renders, the category
 * exists.
 */
export default function CategoryPage() {
  const { locale, t } = useLocale();
  const { categorySlug = '' } = useParams();

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories(locale),
    queryFn: () => publicApi.categories(locale),
    staleTime: STALE.taxonomy,
  });
  const category = categories?.find((row) => row.slug === categorySlug);

  const feed = useFeed(locale, { category: categorySlug, limit: 12 });

  // Only after the taxonomy has loaded: before that, "not found" is just "not yet".
  if (categories && !category) return <NotFound />;

  return (
    <>
      <PageHeader
        title={category?.name ?? categorySlug.replace(/-/g, ' ')}
        subtitle={category?.description ?? undefined}
      />
      <Section>
        <ArticleFeed
          articles={feed.articles}
          isPending={feed.isPending}
          isError={feed.isError}
          hasNextPage={Boolean(feed.hasNextPage)}
          isFetchingNextPage={feed.isFetchingNextPage}
          onLoadMore={() => void feed.fetchNextPage()}
          onRetry={() => void feed.refetch()}
          emptyTitle={t('empty.noStories')}
        />
      </Section>
    </>
  );
}
